import React, { useEffect, useRef, useState } from 'react';
import { VIEW_THEME } from './courseData';

const HolePath = ({ hole, active, dimmed, tee, distance, view, scale, reduced, onSelect, onEnd }) => {
  const playRef = useRef(null);
  const ballRef = useRef(null);
  const onEndRef = useRef(onEnd);
  const [done, setDone] = useState(false);
  const [hover, setHover] = useState(false);
  const theme = VIEW_THEME[view];
  const k = scale;

  useEffect(() => { onEndRef.current = onEnd; }, [onEnd]);

  useEffect(() => {
    setDone(false);
    const path = playRef.current;
    const ball = ballRef.current;
    if (!active || !path) return undefined;
    const len = path.getTotalLength();
    path.style.strokeDasharray = `${len} ${len}`;
    if (reduced) {
      path.style.strokeDashoffset = '0';
      setDone(true);
      const t = setTimeout(() => onEndRef.current?.(hole.number), 900);
      return () => clearTimeout(t);
    }
    path.style.strokeDashoffset = `${len}`;
    const dur = Math.min(2400, 900 + len * 3.2);
    const t0 = performance.now();
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      path.style.strokeDashoffset = `${len * (1 - e)}`;
      const pt = path.getPointAtLength(len * e);
      ball?.setAttribute('cx', pt.x);
      ball?.setAttribute('cy', pt.y);
      if (p < 1) raf = requestAnimationFrame(step);
      else { setDone(true); onEndRef.current?.(hole.number); }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, tee, view, reduced, hole.number]);

  const [tx, ty] = hole.tee;
  const [gx, gy] = hole.green;
  const [bx, by] = hole.badge;
  const chipW = 64 * k;
  let cx = gx + 12 * k;
  let cy = gy - 34 * k;
  if (cx + chipW > 900) cx = gx - chipW - 12 * k;
  if (cy < 44) cy = gy + 12 * k;

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(hole.number); }
  };

  return (
    <g
      role="button"
      tabIndex={0}
      aria-label={`Trou ${hole.number}, par ${hole.par}, ${distance} mètres`}
      aria-pressed={active}
      data-testid={`map-hole-${hole.number}`}
      onClick={() => onSelect(hole.number)}
      onKeyDown={handleKey}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="hole-group outline-none"
      style={{ cursor: 'pointer', opacity: dimmed && !hover ? 0.35 : 1, transition: 'opacity .4s ease' }}
    >
      <path d={hole.d} fill="none" stroke="transparent" strokeWidth={26} />
      <path
        className="hole-focus"
        d={hole.d} fill="none" stroke="#c9a962" strokeWidth={3} opacity={0}
        vectorEffect="non-scaling-stroke" style={{ transition: 'opacity .2s' }}
      />
      <path
        d={hole.d} fill="none" stroke={theme.ink} strokeOpacity={hover || active ? 1 : theme.inkOpacity}
        strokeWidth={hover && !active ? 2 : 1.5} strokeDasharray="5 5" vectorEffect="non-scaling-stroke"
        style={{ transition: 'stroke .5s, stroke-opacity .3s' }}
      />
      <path
        ref={playRef} d={hole.d} fill="none" stroke={theme.play} strokeWidth={3.5}
        strokeLinecap="round" vectorEffect="non-scaling-stroke"
        style={{ opacity: active ? 1 : 0, transition: 'opacity .3s' }}
      />
      <rect
        x={tx - 4 * k} y={ty - 4 * k} width={8 * k} height={8 * k} rx={1.5 * k}
        fill={theme.markerFill} stroke={theme.markerStroke} strokeWidth={1.5} vectorEffect="non-scaling-stroke"
      />
      <circle
        cx={gx} cy={gy} r={5.5 * k}
        fill={active ? '#c9a962' : theme.markerFill} stroke={theme.markerStroke} strokeWidth={1.5}
        vectorEffect="non-scaling-stroke" style={{ transition: 'fill .4s' }}
      />
      {active && !done && !reduced && (
        <circle ref={ballRef} cx={tx} cy={ty} r={4.5 * k} fill={theme.play} />
      )}
      <g transform={`translate(${bx + 11},${by + 9}) scale(${k}) translate(-11,-9)`} style={{ transition: 'transform .3s' }}>
        <rect
          width={22} height={18} rx={4}
          fill={active ? '#c9a962' : theme.badgeFill} stroke={active ? '#c9a962' : theme.markerStroke} strokeWidth={1}
          style={{ transition: 'fill .4s, stroke .4s' }}
        />
        <text
          x={11} y={13} textAnchor="middle" fontSize={12} fontWeight={600} fontFamily="Montserrat, sans-serif"
          fill={active ? '#0f2419' : theme.badgeText} style={{ transition: 'fill .4s', pointerEvents: 'none' }}
        >
          {hole.number}
        </text>
      </g>
      {active && done && (
        <g transform={`translate(${cx},${cy}) scale(${k})`} style={{ animation: 'fadeSlideIn .4s ease-out' }} data-testid="map-hole-chip">
          <rect width={64} height={26} rx={13} fill="rgba(15,36,25,0.92)" stroke="#c9a962" strokeWidth={1} />
          <text x={32} y={17} textAnchor="middle" fontSize={11} fontWeight={500} fontFamily="Montserrat, sans-serif" fill="#e8d48a" letterSpacing={0.8}>
            Trou {hole.number}
          </text>
        </g>
      )}
    </g>
  );
};

export default HolePath;
